import type { ActionContext, ActionTree } from 'vuex';
import {
  type IAssessmentState,
  type IAssessmentSubject,
  type ILockSection,
  type IReactHomeRoom,
  PerformanceLabels,
  type IPerformance,
  type ISaveAnswerResponseData,
  type IAssessmentPctReview,
  type IAssessmentReviewQuestion,
  type IQuestionExplanation,
  type IVideoReturn,
} from '@/store/modules/assessment/types';
import type { IAnswer, IAnswersPayload, RootState } from '@/store/types';
import api from '@/ApiCaller';
import apiReactStudentCaller from '@/ApiReactStudentCaller';

export const REACT_TAKE_SECTION_MISSING_TARGET = 'A section id or standard type is required to start a ReAct session';
import { getTrpcClient, trpcCall } from '@/trpc';
import ServerError from '@/errors/ServerError';
import LockedSectionError from '@/errors/LockedSectionError';
import ForcedFinishSectionError from '@/errors/ForcedFinishSectionError';
import router from '@/router';

type AssessmentActionContext = ActionContext<IAssessmentState, RootState>;
type AssessmentActionTree = ActionTree<IAssessmentState, RootState>;

// Errors that must never trigger the takeSection <-> loadSection auto-redirect: the
// section is stuck in a non-completed, non-takeable state (TDM lock or a proctor
// force-finish), so bouncing between the two routes would loop forever. Let these
// propagate so ErrorBoundary shows the "section is locked" message instead.
const isSectionBlockedError = (err: unknown): boolean =>
  err instanceof LockedSectionError || err instanceof ForcedFinishSectionError;

export const actions: AssessmentActionTree = {
  async fetchAssessments(context: AssessmentActionContext): Promise<void> {
    const assessments = await api.get(`${context.rootState.productPrefix}assessments/available`);
    context.commit('setAssessments', assessments.data);
  },

  async fetchRelevance(context: AssessmentActionContext, filter: any): Promise<void> {
    const relevance =
      Object.keys(filter).length > 0 && filter.lesson_id !== 'all'
        ? await api.get(`${context.rootState.productPrefix}assessments/relevance/${filter.subject}/${filter.lesson_id}`)
        : await api.get(`${context.rootState.productPrefix}assessments/relevance`);

    context.commit('setRelevance', relevance.data);
  },

  async fetchCorrectIncorrect(context: AssessmentActionContext, filter: any): Promise<void> {
    const correctIncorrect =
      Object.keys(filter).length > 0 && filter.lesson_id !== 'all'
        ? await api.get(
            `${context.rootState.productPrefix}assessments/correct-incorrect/${filter.subject}/${filter.lesson_id}`
          )
        : await api.get(`${context.rootState.productPrefix}assessments/correct-incorrect`);

    context.commit('setCorrectIncorrect', correctIncorrect.data);
  },

  async fetchPctReview(context: AssessmentActionContext, subject: any): Promise<void> {
    const unqid = subject.unqid || context.state.section?.unqid;
    if (unqid) {
      const data = await trpcCall(() =>
        getTrpcClient().review.getPctReviewed.query({ unqid })
      );
      context.commit('setPctReviewed', data);
      return;
    }

    let str = `${context.rootState.productPrefix}assessment/pct-reviewed/${subject.subject}/${subject.testNumber}/${subject.sectionNumber}`;

    if (subject.grade) {
      str += `/${subject.grade}`;
    }

    if (subject.courseType) {
      str += `/${subject.courseType}`;
    }

    const pctReviewed = await api.get(str);
    context.commit('setPctReviewed', pctReviewed.data);
  },

  async react_fetchPctReview(context: AssessmentActionContext, subject: any): Promise<void> {
    const pctReviewed = await apiReactStudentCaller.get(`assessment/pct-reviewed/${subject.uSectionId}`);
    const data = pctReviewed.data as { pct_vid_all: number; pct_vid_review: number };
    const pctReview = {
      pct_reviewed_all: data.pct_vid_all || 0,
      pct_reviewed_recommended: data.pct_vid_review || 0,
    } as IAssessmentPctReview;

    context.commit('setPctReviewed', pctReview);
  },

  async takeSection(context: AssessmentActionContext, params: any): Promise<void> {
    if (params.unqid) {
      try {
        const trpcContent = await trpcCall(() => getTrpcClient().assessment.takeSection.query({ unqid: params.unqid }));
        context.commit('setSection', trpcContent);
        return;
      } catch (err) {
        if (err instanceof ServerError && err.status === 404) {
          await router.push('/');
          return;
        }
        if (isSectionBlockedError(err)) {
          throw err;
        }
        if (err instanceof ServerError && err.status === 403) {
          await router.replace(`/assessment/review/${params.unqid}`);
          return;
        }
        throw err;
      }
    }

    if (!params.subject || !params.testNumber || !params.sectionNumber) {
      throw new Error(
        `takeSection: missing required params (subject=${params.subject}, testNumber=${params.testNumber}, sectionNumber=${params.sectionNumber})`
      );
    }

    let str = `${context.rootState.productPrefix}assessment/take-section/${params.subject}/${params.testNumber}/${params.sectionNumber}`;

    if (params.grade) {
      str += `/${params.grade}`;
    }

    if (params.courseType) {
      str += `/${params.courseType}`;
    }

    const content = await api.get(str);

    context.commit('setSection', content.data);
  },

  async react_takeSection(context: AssessmentActionContext, params: any): Promise<void> {
    const standardType = params.standardType ?? params.subject;
    const subject = params.standardType ? params.subject : params.testNumber;

    // Without a standard type (or section id) the URL would be `assessment/take-section/`, which the
    // react API rejects. Fail fast instead of burning the retry budget on a request that cannot succeed.
    if (!standardType) {
      throw new Error(REACT_TAKE_SECTION_MISSING_TARGET);
    }

    let url = `assessment/take-section/${standardType}`;

    url += subject ? `/${subject}` : '';
    url += params.sectionNumber ? `/${params.sectionNumber}` : '';

    const content = await apiReactStudentCaller.get(url);
    context.commit('setSection', content.data);
  },

  async reviewSection(context: AssessmentActionContext, params: any): Promise<void> {
    try {
      const data = await trpcCall(() =>
        (getTrpcClient() as any).review.loadSection.query({ unqid: params.unqid })
      );
      context.commit('setSection', data);
    } catch (err) {
      if (err instanceof ServerError && err.status === 404) {
        await router.push('/');
        return;
      }
      if (isSectionBlockedError(err)) {
        throw err;
      }
      if (err instanceof ServerError && err.status === 403) {
        await router.replace(`/assessment/${params.unqid}`);
        return;
      }
      throw err;
    }
  },

  async react_reviewSection(context: AssessmentActionContext, params: any): Promise<void> {
    const content = await apiReactStudentCaller.get(`assessment/review-section/${params.uSectionId}`);
    context.commit('setSection', content.data);
    context.commit('setAnsweredQuestions', (content.data as any).answers);
  },

  async fetchAnsweredQuestions(context: AssessmentActionContext, params: any): Promise<void> {
    const unqid = params.unqid || context.state.section?.unqid;
    if (unqid) {
      const data = await trpcCall(() =>
        getTrpcClient().review.getReviewQuestions.query({ unqid })
      );
      context.commit('setAnsweredQuestions', data);
      return;
    }

    let str = `${context.rootState.productPrefix}assessment/review-questions/${params.subject}/${params.testNumber}/${params.sectionNumber}`;

    if (params.grade) {
      str += `/${params.grade}`;
    }

    if (params.courseType) {
      str += `/${params.courseType}`;
    }

    const content = await api.get(str);

    context.commit('setAnsweredQuestions', content.data);
  },

  async react_fetchAnsweredQuestions(context: AssessmentActionContext, params: any): Promise<void> {
    const { data } = await apiReactStudentCaller.get(`assessment/review-questions/${params.uSectionId}`);
    const questions = data as IAssessmentReviewQuestion[];
    context.commit('setAnsweredQuestions', questions);
  },

  async fetchAssessmentFeedback(context: AssessmentActionContext, params: any): Promise<void> {
    const unqid = params.unqid || context.state.section?.unqid;
    if (unqid) {
      const data = await trpcCall(() =>
        getTrpcClient().review.getFeedback.query({ unqid })
      );
      context.commit('setAssessmentFeedback', data);
      return;
    }

    const content = await api.get(
      `${context.rootState.productPrefix}assessment/feedback/${params.subject}/${params.testNumber}/${params.sectionNumber}`
    );
    context.commit('setAssessmentFeedback', content.data);
  },

  async react_fetchAssessmentFeedback(context: AssessmentActionContext, params: any): Promise<void> {
    const content = await apiReactStudentCaller.get(`assessment/feedback/${params.uSectionId}`);
    context.commit('setAssessmentFeedbackStandards', content.data);
  },

  async fetchQuestionLessons(context: AssessmentActionContext, question_id: number): Promise<void> {
    const data = await trpcCall(() =>
      getTrpcClient().review.getQuestionLessons.query({ question_id })
    );
    context.commit('setQuestionLessons', data);
  },

  async reviewQuestion(context: AssessmentActionContext, payload: IVideoReturn): Promise<void> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      await trpcCall(() =>
        getTrpcClient().review.questionViewed.mutate({
          unqid,
          question_id: payload.question_id,
          question_num: payload.num ?? 0,
        })
      );
      return;
    }

    await api.post(`${context.rootState.productPrefix}assessment/question-viewed`, payload);
  },

  async react_reviewQuestion(context: AssessmentActionContext, payload: Record<string, unknown>): Promise<void> {
    void context;
    await apiReactStudentCaller.post(`assessment/question-viewed`, payload);
  },

  async feedbackQuestion(context: AssessmentActionContext, payload: Record<string, unknown>): Promise<void> {
    await api.post(`${context.rootState.productPrefix}assessment/question-feedback`, payload);
  },

  async markLessonViewed(context: AssessmentActionContext, payload: { remedial_id: number }): Promise<void> {
    const unqid = context.state.section?.unqid;
    if (!unqid) return;
    await trpcCall(() =>
      getTrpcClient().review.markLessonViewed.mutate({
        unqid,
        remedial_id: payload.remedial_id,
      })
    );
  },

  async saveAnswer(context: AssessmentActionContext, payload: IAnswer): Promise<string> {
    if (payload.unqid) {
      await trpcCall(() =>
        getTrpcClient().assessment.saveAnswer.mutate({
          unqid: payload.unqid ?? '',
          question_id: payload.question_id,
          question_num: payload.question_num,
          time_spent: payload.time_spent,
          time_remain: payload.time_remain,
          answer: payload.answer,
          flagged: !!payload.flagged,
        })
      );
      return '';
    }

    const response = await api.post(`${context.rootState.productPrefix}assessment/save-answer`, payload);
    const data = response.data as Record<'value', string>;
    return data?.value || '';
  },

  async react_saveAnswer(context: AssessmentActionContext, payload: IAnswer): Promise<void> {
    const response = await apiReactStudentCaller.post(`assessment/save-answer`, payload);
    const data = response.data as ISaveAnswerResponseData;
    if (data.status === 'extended') {
      await context.dispatch('contentModule/addMoreContentQuestions', data?.questions || [], { root: true });
      await context.dispatch('contentModule/addMoreContentPassages', data?.passages || [], { root: true });
      context.commit('updateSectionCt', data.questions?.length || 0);
    }
  },

  async saveAnswers(context: AssessmentActionContext, payload: IAnswersPayload): Promise<void> {
    if (payload.unqid) {
      await trpcCall(() =>
        getTrpcClient().assessment.saveAnswers.mutate({
          unqid: payload.unqid ?? '',
          answers: payload.answers.map((a) => ({
            question_id: a.question_id,
            question_num: a.question_num,
            time_spent: a.time_spent,
            time_remain: a.time_remain,
            answer: a.answer,
            flagged: !!a.flagged,
          })),
        })
      );
      return;
    }

    await api.post(`${context.rootState.productPrefix}assessment/save-answers`, payload);
  },

  async react_saveAnswers(context: AssessmentActionContext, payload: IAnswersPayload): Promise<void> {
    await apiReactStudentCaller.post(`assessment/save-answers`, payload);
  },

  async finishSection(
    context: AssessmentActionContext,
    payload: {
      unqid?: string | null;
      usectionId: number;
      timeRemain: number;
      is_force_finished: 1 | 0;
      force_finished_by: number;
      is_offline: 1 | 0;
    }
  ): Promise<void> {
    if (payload.unqid) {
      const result = await trpcCall(() =>
        getTrpcClient().assessment.finishSection.mutate({
          unqid: payload.unqid ?? '',
          isForceFinished: !!payload.is_force_finished,
          forceFinishedBy: payload.force_finished_by,
          isOffline: !!payload.is_offline,
        })
      );

      const { section } = context.state;
      if (section) {
        context.commit('setSection', {
          ...section,
          is_force_finished: payload.is_force_finished,
          is_completed: 1,
          next_unqid: (result as any)?.next_unqid ?? section.next_unqid,
        });
      }
      return;
    }

    await api.post(`${context.rootState.productPrefix}assessment/finish-section`, {
      usection_id: payload.usectionId,
      time_remain: payload.timeRemain,
      is_force_finished: payload.is_force_finished,
      force_finished_by: payload.force_finished_by,
      is_offline: payload.is_offline,
    });

    const { section } = context.state;
    if (section) {
      context.commit('setSection', {
        ...section,
        is_force_finished: payload.is_force_finished,
        is_completed: 1,
      });
    }
  },

  async react_finishSection(
    context: AssessmentActionContext,
    payload: {
      usectionId: number;
      timeRemain: number;
      is_force_finished: 1 | 0;
      force_finished_by: number;
      is_offline: 1 | 0;
    }
  ): Promise<void> {
    await apiReactStudentCaller.post(`assessment/finish-section`, {
      usection_id: payload.usectionId,
      time_remain: payload.timeRemain,
      is_force_finished: payload.is_force_finished,
      force_finished_by: payload.force_finished_by,
      is_offline: payload.is_offline,
    });

    const { section } = context.state;
    if (section) {
      context.commit('setSection', {
        ...section,
        is_force_finished: payload.is_force_finished,
        is_completed: 1,
      });
    }
  },

  async handleQuestionFeedback(
    _: AssessmentActionContext,
    payload: {
      question_num: number;
      usection_id: number;
      question_id: number;
      feedback: string;
    }
  ): Promise<void> {
    const unqid = _.state.section?.unqid;
    if (unqid) {
      await trpcCall(() =>
        getTrpcClient().review.postQuestionFeedback.mutate({
          unqid,
          question_id: payload.question_id,
          question_num: payload.question_num,
          feedback: payload.feedback as 'i' | 'c',
        })
      );
      return;
    }

    await api.post(`${_.rootState.productPrefix}assessment/question-feedback`, {
      question_num: payload.question_num,
      usection_id: payload.usection_id,
      question_id: payload.question_id,
      feedback: payload.feedback,
    });
  },

  async react_handleQuestionFeedback(
    _: AssessmentActionContext,
    payload: {
      usection_id: string;
      uquestion_id: number;
      feedback: string;
    }
  ): Promise<void> {
    await apiReactStudentCaller.post(`assessment/question-feedback`, {
      usection_id: payload.usection_id,
      question_id: payload.uquestion_id,
      feedback: payload.feedback,
    });
  },

  async takePracticeSection(context: AssessmentActionContext, params: any): Promise<void> {
    const content = await api.get(`practice/section/${params.subject}`);
    context.commit('setSection', content.data);
  },

  async lockSection(context: AssessmentActionContext, payload: ILockSection): Promise<void> {
    await api.post(`${context.rootState.productPrefix}assessment/lock-section`, {
      usection_id: payload.usection_id,
      is_locked: payload.is_locked,
      locked_by: payload.locked_by,
    });
  },

  async setSectionTime(context: AssessmentActionContext, time: number): Promise<void> {
    context.commit('setSectionTime', time);
  },

  async setQuestionTime(
    context: AssessmentActionContext,
    timeValues: { time_spent: number; time_remain: number }
  ): Promise<void> {
    context.commit('setQuestionTime', timeValues);
  },

  async setQuestionExplanation(
    context: AssessmentActionContext,
    payload: { questionId: number; explanation: IQuestionExplanation }
  ): Promise<void> {
    context.commit('setQuestionExplanation', payload);
  },

  async react_fetchHomeRoom(context: AssessmentActionContext): Promise<void> {
    const response = await apiReactStudentCaller.get(`assessment/examroom`);
    const data = response.data as IReactHomeRoom;
    const performanceResult = data.performance || [];
    const sections = data.sections || [];
    const assignments = data.assignments || [];
    const performance: IPerformance = [];

    if (performanceResult.length) {
      performanceResult.forEach((item) => {
        const subject = item.subject === 'Total' ? 'overall' : item.subject;
        performance.push({
          subject: subject,
          label: PerformanceLabels[subject as keyof typeof PerformanceLabels],
          performance: item.performance,
        });
      });
    }

    context.commit('setAssessments', sections);
    context.commit('setPerformance', performance);
    context.commit('setAssignments', assignments);
    context.commit('setOpenSessionSubjects', data.open_session_subjects || []);
    context.commit('setOpenSessionStandardType', data.open_session_standard_type || null);
  },
};
